// src/components/Layout.js

import React, { useState } from 'react';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar, Typography, Divider, AppBar, Toolbar, IconButton, Badge, Menu, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChatIcon from '@mui/icons-material/Chat';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import InfoIcon from '@mui/icons-material/Info';
import MailIcon from '@mui/icons-material/Mail';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import QuizIcon from '@mui/icons-material/Quiz';
import { useAuth } from '../context/AuthContext';
import { auth } from '../config/firebase';
import { signOut } from 'firebase/auth';
import FindPeopleDialog from './FindPeopleDialog';
import RequestsList from './RequestsList';
import ConnectionsList from './ConnectionsList';
import ChatWindow from './ChatWindow';
import Dashboard from './Dashboard';
import AboutDialog from './AboutDialog';
import EditProfileDialog from './EditProfileDialog';
import PrivacyDialog from './PrivacyDialog';
import FAQDialog from './FAQDialog';
import BlockedUsersDialog from './BlockedUsersDialog'; // <-- 2. IMPORT NEW DIALOG
import { usePresence } from '../hooks/usePresence';
import { useRequestCount } from '../hooks/useRequestCount';
import { useTotalUnreadCount } from '../hooks/useTotalUnreadCount';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';


const drawerWidth = 280;

const getInitials = (name = '') => {
    return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
};

const Layout = () => {
  const { currentUser } = useAuth();
  usePresence();
  const requestCount = useRequestCount();
  const totalUnreadCount = useTotalUnreadCount();

  const [mainView, setMainView] = useState('dashboard');
  const [findPeopleOpen, setFindPeopleOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [blockedUsersOpen, setBlockedUsersOpen] = useState(false); // <-- 3. ADD STATE
  const [activeChat, setActiveChat] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleSignOut = () => signOut(auth);

  const handleSelectChat = (user) => {
    setActiveChat(user);
    if (isMobile) setMobileOpen(false);
  };

  const handleCloseChat = () => setActiveChat(null);
  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleEditProfileOpen = () => { setEditProfileOpen(true); handleMenuClose(); };
  
  const handleBlockedUsersOpen = () => {
    setBlockedUsersOpen(true);
    handleMenuClose();
  };

  const handleViewChange = (view) => {
    setActiveChat(null);
    setMainView(view);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const renderMainContent = () => {
    if (activeChat) return <ChatWindow chatPartner={activeChat} onBack={handleCloseChat} />;
    switch (mainView) {
      case 'dashboard': return <Dashboard />;
      case 'chats': return <ConnectionsList onSelectChat={handleSelectChat} />;
      case 'requests': return <RequestsList />;
      default: return <Dashboard />;
    }
  };

  const drawerContent = (
    <div>
      <Toolbar sx={{ padding: '16px !important' }}><Typography variant="h5" noWrap component="div">Zinlanka™</Typography></Toolbar>
      <ListItemButton onClick={handleMenuClick} sx={{ m: 1, borderRadius: 2 }}>
        <Avatar src={currentUser?.photoURL} sx={{ mr: 2 }}>
            {getInitials(currentUser?.displayName)}
        </Avatar>
        <ListItemText primary={currentUser?.displayName} />
      </ListItemButton>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        <ListItem disablePadding><ListItemButton onClick={() => handleViewChange('dashboard')}><ListItemIcon><DashboardIcon /></ListItemIcon><ListItemText primary="Dashboard" /></ListItemButton></ListItem>
        <ListItem disablePadding><ListItemButton onClick={() => handleViewChange('chats')}><ListItemIcon><Badge badgeContent={totalUnreadCount} color="error"><ChatIcon /></Badge></ListItemIcon><ListItemText primary="Chats" /></ListItemButton></ListItem>
        <ListItem disablePadding><ListItemButton onClick={() => handleViewChange('requests')}><ListItemIcon><Badge badgeContent={requestCount} color="error"><MailIcon /></Badge></ListItemIcon><ListItemText primary="Requests" /></ListItemButton></ListItem>
        <ListItem disablePadding><ListItemButton onClick={() => setFindPeopleOpen(true)}><ListItemIcon><PeopleIcon /></ListItemIcon><ListItemText primary="Find People" /></ListItemButton></ListItem>
      </List>
      <Divider />
      <List>
        <ListItem disablePadding><ListItemButton onClick={() => setFaqOpen(true)}><ListItemIcon><QuizIcon /></ListItemIcon><ListItemText primary="FAQ" /></ListItemButton></ListItem>
        <ListItem disablePadding><ListItemButton onClick={() => setPrivacyOpen(true)}><ListItemIcon><PrivacyTipIcon /></ListItemIcon><ListItemText primary="Privacy" /></ListItemButton></ListItem>
        <ListItem disablePadding><ListItemButton onClick={() => setAboutOpen(true)}><ListItemIcon><InfoIcon /></ListItemIcon><ListItemText primary="About" /></ListItemButton></ListItem>
        <ListItem disablePadding><ListItemButton onClick={handleSignOut}><ListItemIcon><LogoutIcon /></ListItemIcon><ListItemText primary="Logout" /></ListItemButton></ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ display: { md: 'none' } }}>
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}><MenuIcon /></IconButton>
          <Typography variant="h6" noWrap component="div">{activeChat ? activeChat.displayName : 'Zinlanka™'}</Typography>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer variant={isMobile ? 'temporary' : 'permanent'} open={isMobile ? mobileOpen : true} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, display: 'flex', flexDirection: 'column' } }}>
          {drawerContent}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 0, width: { md: `calc(100% - ${drawerWidth}px)` }, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Toolbar sx={{ display: { md: 'none' } }} />
        <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>{renderMainContent()}</Box>
      </Box>
      
      {/* --- 4. ADD NEW MENU ITEM --- */}
      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditProfileOpen}>Edit Profile</MenuItem>
        <MenuItem onClick={handleBlockedUsersOpen}>Blocked Users</MenuItem>
      </Menu>

      <FindPeopleDialog open={findPeopleOpen} onClose={() => setFindPeopleOpen(false)} />
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <EditProfileDialog open={editProfileOpen} onClose={() => setEditProfileOpen(false)} />
      <PrivacyDialog open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <FAQDialog open={faqOpen} onClose={() => setFaqOpen(false)} />
      {/* --- 5. RENDER THE NEW DIALOG --- */}
      <BlockedUsersDialog open={blockedUsersOpen} onClose={() => setBlockedUsersOpen(false)} />
    </Box>
  );
};

export default Layout;